/**
 * Revcall / Xdial legacy API surface — typed wrapper over `client.ts`.
 *
 * Public surface preserved (zero migration cost for callers):
 *   api.entities.<EntityName>.{filter,list,get,create,bulkCreate,update,delete,count,aggregate}
 *   api.functions.invoke(name, params)
 *   api.integrations.Core.{InvokeLLM,SendEmail,SendSMS,...}
 *   api.auth.{me,login,logout,redirectToLogin,clientMe,clientLogin,clientLogout}
 *   api.appLogs.logUserInApp
 *
 * Implementation: every call routes through `apiClient` (client.ts) so the
 * single-flight refresh, structured-error handling, and JWT v2 redirect
 * branches are shared with the typed endpoints in `src/api/endpoints/*`.
 *
 * The legacy js client is gone — this file is the canonical legacy entrypoint.
 * Migrating call sites to typed `endpoints/` is tracked separately
 * (REFACTORING_FRONTEND_PLAN.md §16).
 */

import { apiClient, ApiErrorException } from './client';

// QA-W7-13: same rule as client.ts (kept local: tests mock ./client).
const isExpectedStatus = (status: number | undefined) => status === 401 || status === 403;

export const API_BASE: string = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';

// ─── Entity CRUD factory ────────────────────────────────────────────────────

interface EntityFilterResult {
  data?: unknown[];
}

/**
 * Le client d'entités. Les lectures sont GÉNÉRIQUES : un appelant qui sait ce
 * qu'il attend l'écrit, `api.entities.ParallelCall.filter<ParallelCall>(…)`,
 * et le compilateur le tient.
 *
 * Le défaut par défaut reste `unknown`, donc les appelants existants ne
 * changent pas. C'est ce qui permet de retirer `@ts-nocheck` fichier par
 * fichier au lieu d'un big bang : sans type de retour, un fichier typé se
 * remplit d'erreurs dès la première lecture et on renonce.
 */
interface EntityClient {
  filter: <T = unknown>(filter?: Record<string, unknown>, sort?: string, limit?: number) => Promise<T[]>;
  list: <T = unknown>(sort?: string) => Promise<T[]>;
  get: <T = unknown>(id: string) => Promise<T>;
  create: (data: unknown) => Promise<unknown>;
  bulkCreate: (items: unknown[]) => Promise<unknown>;
  update: (id: string, data: unknown) => Promise<unknown>;
  delete: (id: string) => Promise<unknown>;
  count: (filter?: Record<string, unknown>) => Promise<number>;
  aggregate: (pipeline: unknown[]) => Promise<unknown>;
  // Only present on ProspectQueue — pre-import blacklist lookup
  checkBlacklist?: (payload: { phones: string[] }) => Promise<{ blacklisted: string[] }>;
  // Only present on ProspectQueue — search across every list of a customer
  search?: <T = unknown>(payload: { q: string; customer_id?: string; limit?: number }) => Promise<T[]>;
}

function createEntityClient(entityName: string): EntityClient {
  const base = `/entities/${entityName}`;
  const client: EntityClient = {
    // Le serveur rend soit un tableau, soit une enveloppe { data }. Le cast
    // final est le SEUL endroit où l'on affirme la forme : au-delà, le type
    // demandé par l'appelant circule normalement.
    filter: async <T = unknown>(filter = {}, sort?: string, limit?: number): Promise<T[]> => {
      const result = await apiClient.post<unknown[] | EntityFilterResult>(`${base}/filter`, {
        filter,
        sort,
        limit,
      });
      if (Array.isArray(result)) return result as T[];
      return (result?.data ?? []) as T[];
    },

    list: async <T = unknown>(sort?: string): Promise<T[]> => {
      const result = await apiClient.get<unknown[] | EntityFilterResult>(base, {
        query: sort ? { sort } : undefined,
      });
      if (Array.isArray(result)) return result as T[];
      return (result?.data ?? []) as T[];
    },

    get: (id) => apiClient.get(`${base}/${id}`),

    create: (data) => apiClient.post(base, data),

    bulkCreate: (items) => apiClient.post(`${base}/bulk`, items),

    update: (id, data) => apiClient.patch(`${base}/${id}`, data),

    delete: (id) => apiClient.delete(`${base}/${id}`),

    count: async (filter = {}) => {
      const r = await apiClient.post<{ count: number }>(`${base}/count`, { filter });
      return r.count;
    },

    aggregate: (pipeline) => apiClient.post(`${base}/aggregate`, { pipeline }),
  };
  if (entityName === 'ProspectQueue') {
    client.checkBlacklist = (payload) =>
      apiClient.post<{ blacklisted: string[] }>(`${base}/check-blacklist`, payload);
    client.search = <T = unknown>(payload: { q: string; customer_id?: string; limit?: number }) =>
      apiClient.post<T[]>(`${base}/search`, payload);
  }
  return client;
}

// ─── Functions invoke → REST API mapping ────────────────────────────────────

type HttpVerb = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
interface FunctionRoute {
  method: HttpVerb;
  path: string;
  body?: unknown;
}
type FunctionMapper = (params?: Record<string, unknown>) => FunctionRoute;

const enc = encodeURIComponent;
const optClient = (p?: Record<string, unknown>): string =>
  p?.clientEmail ? `?clientEmail=${enc(String(p.clientEmail))}` : '';

const FUNCTION_MAP: Record<string, FunctionMapper> = {
  // Auth (Caller)
  callerMe:                    () => ({ method: 'GET', path: '/auth/me' }),
  loginCaller:                 (p) => ({ method: 'POST', path: '/auth/login', body: p }),
  logoutCaller:                () => ({ method: 'POST', path: '/auth/logout' }),

  // Auth (Client)
  clientLogin:                 (p) => ({ method: 'POST', path: '/auth/client-login', body: p }),
  clientMe:                    () => ({ method: 'GET', path: '/auth/client-me' }),
  clientLogout:                () => ({ method: 'POST', path: '/auth/logout' }),

  // Twilio tokens
  generateCallerToken:         (p) => ({ method: 'POST', path: '/twilio/token', body: p }),
  generateTwilioToken:         (p) => ({ method: 'POST', path: '/twilio/token', body: p }),

  // Twilio operations
  parallelDialProspectsPublic: (p) => ({ method: 'POST', path: '/twilio/parallel-dial', body: p }),
  twilioHangupCall:            (p) => ({ method: 'POST', path: '/twilio/hangup', body: p }),
  twilioConferenceRoom:        (p) => ({ method: 'POST', path: '/twilio/conference-room', body: p }),
  twilioCardTiming:            (p) => ({ method: 'POST', path: '/twilio/card-timing', body: p }),
  twilioAudioTiming:           (p) => ({ method: 'POST', path: '/twilio/audio-timing', body: p }),
  twilioHangupAllMine:          () => ({ method: 'POST', path: '/twilio/hangup-all-mine', body: {} }),
  twilioHoldCall:              (p) => ({ method: 'POST', path: '/twilio/hold', body: p }),
  twilioSendSMS:               (p) => ({ method: 'POST', path: '/twilio/sms', body: p }),
  twilioListCalls:             (p) => ({ method: 'GET', path: `/twilio/calls?limit=${(p?.limit as number) || 50}` }),
  twilioListMessages:          (p) => ({ method: 'GET', path: `/twilio/messages?limit=${(p?.limit as number) || 50}` }),
  // Le fil d'un prospect : entrant ET sortant. Optionnel: phone filter.
  listInboundSms:              (p) => ({ method: 'GET', path: `/telephony/inbound-sms?limit=${(p?.limit as number) || 50}${p?.phone ? `&phone=${enc(String(p.phone))}` : ''}` }),
  // Nombre de SMS par prospect, pour la colonne SMS de la table des contacts.
  getSmsCounts:                 () => ({ method: 'GET', path: '/telephony/sms-counts' }),
  // Les conversations SMS, la plus récente d'abord.
  listSmsThreads:              (p) => ({ method: 'GET', path: `/telephony/sms-threads?limit=${(p?.limit as number) || 50}` }),
  // Numéro personnel d'un membre : enregistrement, vérification, retrait.
  // Le catalogue : ce qu'on peut obtenir, à quel prix, et qui peut le commander.
  listNumberOffers:             () => ({ method: 'GET', path: '/telephony/number-offers' }),
  // Un seul point d'entrée : on dit ce que le numéro doit faire, pas chez qui.
  addNumber:                   (p) => ({ method: 'POST', path: '/telephony/numbers', body: { offer: p?.offer, phone_number: p?.phoneNumber, identity_id: p?.identityId } }),
  // People Zadarma mobile lines are registered to (2 lines per identity).
  listCarrierIdentities:       () => ({ method: 'GET', path: '/telephony/identities' }),
  // Shows a non-Zadarma number under a person (identityId) or no one (null).
  setNumberHolder:             (p) => ({ method: 'PATCH', path: `/telephony/numbers/${enc(String(p?.id))}/holder`, body: { identity_id: p?.identityId ?? null } }),
  // p.form: FormData with the identity fields and `documents` files.
  createCarrierIdentity:       (p) => ({ method: 'POST', path: '/telephony/identities', body: p?.form }),
  removeCarrierIdentity:       (p) => ({ method: 'DELETE', path: `/telephony/identities/${enc(String(p?.id))}` }),
  listApiKeys:                 () => ({ method: 'GET', path: '/api-keys' }),
  createApiKey:                (p) => ({ method: 'POST', path: '/api-keys', body: { name: p?.name, ...(p?.membershipId ? { membership_id: p.membershipId } : {}) } }),
  revokeApiKey:                (p) => ({ method: 'DELETE', path: `/api-keys/${enc(String(p?.id))}` }),
  startPersonalNumber:         (p) => ({ method: 'POST', path: '/telephony/numbers/personal', body: { phone_number: p?.phoneNumber } }),
  checkPersonalNumber:         (p) => ({ method: 'POST', path: `/telephony/numbers/personal/${enc(String(p?.id))}/check`, body: {} }),
  removePersonalNumber:        (p) => ({ method: 'DELETE', path: `/telephony/numbers/personal/${enc(String(p?.id))}` }),
  twilioListPhoneNumbers:      () => ({ method: 'GET', path: '/twilio/phone-numbers' }),
  getTwilioCallStatus:         (p) => ({ method: 'POST', path: '/twilio/call-status', body: { callSids: p?.callSids ?? [p?.callSid] } }),
  twilioStartRecording:        (p) => ({ method: 'POST', path: '/twilio/recording/start', body: p }),
  twilioStopRecording:         (p) => ({ method: 'POST', path: '/twilio/recording/stop', body: p }),
  twilioCreateConference:      (p) => ({ method: 'POST', path: '/twilio/manual-call', body: p }),
  twilioTestConnection:        () => ({ method: 'GET', path: '/twilio/test-connection' }),
  configureIncomingWebhook:    (p) => ({ method: 'POST', path: '/twilio/configure-webhook', body: p }),
  syncTwilioRecordings:        (p) => ({ method: 'POST', path: '/twilio/sync-recordings', body: p }),

  // SaaS multi-tenant /api/telephony/numbers
  listTenantNumbers:           () => ({ method: 'GET', path: '/telephony/numbers' }),
  // Voicemails inbound (messages laisses sur les numeros du tenant).
  listInboundVoicemails:       (p) => ({ method: 'GET', path: `/telephony/voicemails${p?.limit ? `?limit=${p.limit}` : ''}` }),
  // Per-tenant caller preferences (active outgoing numbers)
  updateMembershipPrefs:       (p) => ({ method: 'PATCH', path: '/auth/membership-prefs', body: { selectedCallerIds: p?.selectedCallerIds ?? [] } }),
  // mode=detach : le numéro quitte Revcall, la ligne reste chez l'opérateur.
  // mode=release : la ligne est rendue, et ce n'est pas réversible.
  releaseTenantNumber:        (p) => ({ method: 'DELETE', path: `/telephony/numbers/${enc(String(p?.id))}?mode=${p?.mode === 'detach' ? 'detach' : 'release'}` }),
  reassignTwilioNumber:        (p) => ({ method: 'PATCH', path: `/telephony/admin/numbers/${enc(String(p?.id))}/reassign`, body: { tenant_email: p?.tenantEmail } }),
  syncTwilioNumbers:           (p) => ({ method: 'POST', path: '/telephony/admin/numbers/sync', body: { default_tenant_email: p?.defaultTenantEmail } }),
  getTwilioUsageReport:        (p) => ({ method: 'GET', path: `/twilio/admin/usage${p?.month ? `?month=${enc(String(p.month))}` : ''}` }),

  // Billing / Stripe
  getBillingSubscription:      () => ({ method: 'GET', path: '/billing/subscription' }),
  getBillingUsage:             (p) => ({ method: 'GET', path: `/billing/usage?days=${(p?.days as number) || 30}` }),
  createCheckoutSession:       (p) => ({ method: 'POST', path: '/billing/checkout-session', body: { plan_id: p?.plan_id } }),
  createPortalSession:         () => ({ method: 'POST', path: '/billing/portal-session' }),

  // Call recordings & transcription
  getCallRecording:            (p) => ({ method: 'POST', path: '/twilio/recording/get', body: p }),
  transcribeCall:              (p) => ({ method: 'POST', path: '/ai/transcribe', body: p }),

  // AI features
  scoreCall:                   (p) => ({ method: 'POST', path: '/ai/score-call', body: p }),
  generateVmScript:            (p) => ({ method: 'POST', path: '/ai/generate-vm-script', body: p }),
  ttsPreview:                  (p) => ({ method: 'POST', path: '/ai/tts-preview', body: p }),
  generateEmail:               (p) => ({ method: 'POST', path: '/ai/generate-email', body: p }),
  sendEmail:                   (p) => ({ method: 'POST', path: '/email/send', body: p }),
  emailCapabilities:           () => ({ method: 'GET', path: '/email/capabilities' }),
  // Per-caller email OAuth status / disconnect (Gmail/Outlook)
  emailIntegrationStatus:      () => ({ method: 'GET', path: '/integrations/email/status' }),
  emailIntegrationDisconnect:  () => ({ method: 'POST', path: '/integrations/email/disconnect', body: {} }),
  analyzeCampaign:             (p) => ({ method: 'POST', path: '/ai/analyze-campaign', body: p }),

  // HubSpot
  syncToHubSpot:               (p) => ({ method: 'POST', path: '/hubspot/sync', body: p }),
  getHubSpotStatus:            (p) => ({ method: 'GET', path: `/hubspot/status${optClient(p)}` }),
  syncHubSpotContacts:         (p) => ({ method: 'POST', path: '/hubspot/sync-contacts', body: p }),
  fetchHubSpotContact:         (p) => ({ method: 'POST', path: '/hubspot/fetch-contact', body: p }),
  syncHubSpotLists:            (p) => ({ method: 'POST', path: '/hubspot/sync-contacts', body: p }),
  getHubSpotSegments:          (p) => ({ method: 'GET', path: `/hubspot/segments/${(p?.listId as string) || 'all'}${optClient(p)}` }),
  getHubSpotLists:             (p) => ({ method: 'GET', path: `/hubspot/lists${optClient(p)}` }),
  hubspotSyncMeetingOutcome:   (p) => ({ method: 'POST', path: '/hubspot/sync-meeting-outcome', body: p }),
  getHubSpotAccountInfo:       (p) => ({ method: 'GET', path: `/hubspot/account${optClient(p)}` }),
  hubspotOAuthDisconnect:      (p) => ({ method: 'POST', path: '/hubspot/oauth/disconnect', body: p }),
  getHubSpotSyncStatus:        (p) => ({ method: 'GET', path: `/hubspot/status${optClient(p)}` }),
  completeHubSpotTask:         (p) => ({ method: 'POST', path: '/hubspot/task/complete', body: p }),
  updateHubSpotTaskDueDate:    (p) => ({ method: 'POST', path: '/hubspot/task/update', body: p }),

  // Calendar
  createGoogleCalendarEvent:   (p) => ({ method: 'POST', path: '/calendar/google/event', body: p }),
  getGoogleAccountInfo:        () => ({ method: 'GET', path: '/calendar/google/account' }),
  getGoogleAvailability:       (p) => ({ method: 'POST', path: '/calendar/google/availability', body: p }),
  listGoogleCalendars:         () => ({ method: 'GET', path: '/calendar/google/calendars' }),
  createCalendlyInvitee:       (p) => ({ method: 'POST', path: '/calendar/calendly/invitee', body: p }),
  getCalendarIntegrations:     () => ({ method: 'GET', path: '/calendar/integrations' }),
  connectCalendar:             (p) => ({ method: 'POST', path: '/calendar/connect', body: p }),
  disconnectCalendar:          (p) => ({ method: 'POST', path: '/calendar/disconnect', body: p }),

  // Analytics
  getClientAggregations:       (p) => ({ method: 'GET', path: `/analytics/aggregations?clientEmail=${enc(String(p?.client_email ?? ''))}` }),
  generateKPIReport:           (p) => ({ method: 'POST', path: '/analytics/kpi', body: p }),

  // AI advanced
  analyzeCallWithAI:           (p) => ({ method: 'POST', path: '/ai/analyze-call', body: p }),
  liveCoach:                   (p) => ({ method: 'POST', path: '/ai/live-coach', body: p }),
  liveCoachBasePrompt:         () => ({ method: 'GET', path: '/ai/live-coach/base-prompt' }),
  getSuggestedActions:         (p) => ({ method: 'GET', path: `/ai/suggested-actions?callerId=${enc(String(p?.callerId ?? ''))}&clientEmail=${enc(String(p?.clientEmail ?? ''))}` }),

  // Caller notifications (ADR-036: first-meeting no-show reported by Swoft)
  getMyNotifications:          () => ({ method: 'GET', path: '/notifications/mine' }),
  // `ids`: mark only the notifications that were shown (the rail); none = all.
  markAllNotificationsRead:    (p) => ({ method: 'POST', path: '/notifications/read-all', body: p?.ids ? { ids: p.ids } : {} }),

  // SWOFT CRM
  getSwoftCRMStatus:           (p) => ({ method: 'GET', path: `/swoft-crm/status${optClient(p)}` }),
  getSwoftCRMDashboard:        (p) => ({ method: 'GET', path: `/swoft-crm/dashboard${optClient(p)}` }),
  getSwoftCRMCustomers:        (p) => ({ method: 'GET', path: `/swoft-crm/customers${optClient(p)}` }),
  getSwoftCRMCustomer:         (p) => ({ method: 'GET', path: `/swoft-crm/customers/${p?.customerId}${optClient(p)}` }),
  // `customerId` nomme le client dont on lit le pipeline : le lien Swoft, donc
  // le CRM interroge, se resout par client. La chaine de requete est construite
  // par URLSearchParams et non a la main : l'ancienne version collait « ? » ou
  // « & » selon la presence de clientEmail, et un parametre de plus ajoute au
  // mauvais endroit passait inapercu.
  getSwoftCRMOpportunities:    (p) => {
    const params = new URLSearchParams();
    if (p?.clientEmail) params.set('clientEmail', String(p.clientEmail));
    if (p?.customerId) params.set('customerId', String(p.customerId));
    if (p?.stage) params.set('stage', String(p.stage));
    const query = params.toString();
    return { method: 'GET', path: `/swoft-crm/opportunities${query ? `?${query}` : ''}` };
  },
  getSwoftCRMOpportunity:      (p) => {
    const params = new URLSearchParams();
    if (p?.clientEmail) params.set('clientEmail', String(p.clientEmail));
    if (p?.customerId) params.set('customerId', String(p.customerId));
    const query = params.toString();
    return { method: 'GET', path: `/swoft-crm/opportunities/${enc(String(p?.opportunityId))}${query ? `?${query}` : ''}` };
  },
  getSwoftCRMAppointments:     (p) => ({ method: 'GET', path: `/swoft-crm/opportunities/${p?.opportunityId}/appointments${optClient(p)}` }),
  // Les echanges d'une opportunite : Swoft les attache a la PERSONNE, donc le
  // serveur passe par le compte de l'opportunite. D'ou le customerId, qui
  // nomme le client dont on lit le CRM.
  getSwoftCRMCommunications:   (p) => {
    const params = new URLSearchParams();
    if (p?.clientEmail) params.set('clientEmail', String(p.clientEmail));
    if (p?.customerId) params.set('customerId', String(p.customerId));
    const query = params.toString();
    return { method: 'GET', path: `/swoft-crm/opportunities/${enc(String(p?.opportunityId))}/communications${query ? `?${query}` : ''}` };
  },
  // Tout l'historique du client, sans se limiter a une opportunite.
  listSwoftCRMCommunications:  (p) => {
    const params = new URLSearchParams();
    if (p?.clientEmail) params.set('clientEmail', String(p.clientEmail));
    if (p?.customerId) params.set('customerId', String(p.customerId));
    if (p?.limit) params.set('limit', String(p.limit));
    const query = params.toString();
    return { method: 'GET', path: `/swoft-crm/communications${query ? `?${query}` : ''}` };
  },

  // Email history
  listEmails:                  (p) => ({ method: 'GET', path: `/email/list${optClient(p)}` }),

  // Admin
  resetClientData:             (p) => ({ method: 'POST', path: '/admin/reset-client-data', body: p }),
  updateCallerSession:         (p) => ({ method: 'PATCH', path: '/auth/update-caller', body: p }),
};

interface InvokeResult<T = unknown> {
  data: T;
  status: number;
}

async function invokeFunction<T = unknown>(
  functionName: string,
  params?: Record<string, unknown>,
): Promise<InvokeResult<T>> {
  const mapper = FUNCTION_MAP[functionName];
  if (!mapper) {
    console.warn(`[api-client] Unknown function: ${functionName}, falling back to POST`);
    const data = await apiClient.post<T>(`/functions/${functionName}`, params);
    return { data, status: 200 };
  }

  const { method, path, body } = mapper(params);
  // eslint-disable-next-line no-console
  console.log(`[api-client] invoke ${functionName} → ${method} ${path}`, body !== undefined ? { bodyKeys: Object.keys((body as object) ?? {}) } : '(no body)');
  let data: T;
  try {
    switch (method) {
      case 'GET':    data = await apiClient.get<T>(path); break;
      case 'POST':   data = await apiClient.post<T>(path, body); break;
      case 'PATCH':  data = await apiClient.patch<T>(path, body); break;
      case 'PUT':    data = await apiClient.put<T>(path, body); break;
      case 'DELETE': data = await apiClient.delete<T>(path); break;
    }
  } catch (err) {
    // QA-W7-13: an expected 401/403 is logged at debug level.
    // eslint-disable-next-line no-console
    (isExpectedStatus((err as { status?: number })?.status) ? console.debug : console.error)(`[api-client] ${functionName} FAILED`, {
      method, path,
      status: (err as { status?: number })?.status,
      code: (err as { error?: { code?: string } })?.error?.code,
      message: (err as Error)?.message,
    });
    throw err;
  }
  // eslint-disable-next-line no-console
  console.log(`[api-client] ${functionName} OK`, typeof data === 'object' && data !== null ? { keys: Object.keys(data as object) } : typeof data);
  return { data: data!, status: 200 };
}

// ─── Integrations ───────────────────────────────────────────────────────────

/**
 * The front features allowed to call POST /ai/invoke (AUDIT-ADM-05): the
 * server refuses any other value, and a viewer may only use the portal
 * assistant.
 */
export type InvokeLLMFeature = 'prospecting_assistant' | 'ai_assistant' | 'call_scripts' | 'performance_coach' | 'cold_call_trainer';
interface InvokeLLMParams {
  feature: InvokeLLMFeature;
  prompt?: string;
  text?: string;
  response_json_schema?: unknown;
}
interface SendEmailParams { to: string; subject: string; body: string; from?: string; }
interface SendSMSParams   { to: string; body?: string; message?: string; }

const integrations = {
  Core: {
    InvokeLLM: (params: InvokeLLMParams) => apiClient.post('/ai/invoke', {
      feature: params?.feature,
      prompt: params?.prompt ?? params?.text ?? '',
      response_json_schema: params?.response_json_schema,
    }),
    SendEmail: (params: SendEmailParams) => apiClient.post('/email/send', {
      to: params?.to, subject: params?.subject, body: params?.body, from: params?.from,
    }),
    SendSMS: (params: SendSMSParams) => apiClient.post('/twilio/sms', {
      to: params?.to, message: params?.body ?? params?.message,
    }),
    UploadFile: (): never => { throw new Error('UploadFile not yet migrated'); },
    GenerateImage: (): never => { throw new Error('GenerateImage not yet migrated'); },
    ExtractDataFromUploadedFile: (): never => {
      throw new Error('ExtractDataFromUploadedFile not yet migrated');
    },
  },
};

// ─── Auth ───────────────────────────────────────────────────────────────────

const auth = {
  me:    () => apiClient.get('/auth/me'),
  login: (username: string, password: string) =>
    apiClient.post('/auth/login', { username, password }),
  // QA-W6-12: after logout we land on the sign-in page, not the marketing
  // site, and without a `?next=` that would confuse the user. The request is
  // awaited first: leaving the page at once could cancel it and keep the
  // session cookies.
  logout: async () => {
    try { await apiClient.post('/auth/logout'); } catch { /* leave anyway */ }
    window.location.href = '/Login';
  },
  redirectToLogin: () => {
    window.location.href = '/';
  },
  clientMe:    () => apiClient.get('/auth/client-me'),
  clientLogin: (email: string, password: string) =>
    apiClient.post('/auth/client-login', { email, password }),
  clientLogout: async () => {
    try { await apiClient.post('/auth/logout'); } catch { /* leave anyway */ }
    window.location.href = '/Login';
  },
};

// ─── Entity registry ────────────────────────────────────────────────────────

// ADR-031 — `Client` and `TeamMember` removed (replaced by Organisation +
// Membership). The remaining `Client*` entries are unrelated nested
// aggregates (notifications/metrics/meetings/onboarding) that belong to a
// tenant but are NOT the tenant identity itself.
const ENTITY_NAMES = [
  'Caller', 'CallerSession', 'ProspectQueue', 'ParallelCall',
  'CallLog', 'ClientMetrics', 'ClientNotification', 'ClientOnboarding',
  'InternalNote', 'AdminSettings', 'Notification',
  'CallerScript', 'ClientMeeting', 'MessageTemplate',
  'MeetingReminderFlag', 'ListSetting', 'SavedView', 'SmartAction', 'DismissedCard', 'LiveFeedObjective',
] as const;

type EntityName = typeof ENTITY_NAMES[number];

const entities = {} as Record<EntityName, EntityClient>;
for (const name of ENTITY_NAMES) {
  // Map 'Notification' to 'ClientNotification' on the backend
  const backendName = name === 'Notification' ? 'ClientNotification' : name;
  entities[name] = createEntityClient(backendName);
}

// ─── Public surface ─────────────────────────────────────────────────────────

export const api = {
  entities,
  functions: { invoke: invokeFunction },
  integrations,
  auth,
  appLogs: { logUserInApp: () => Promise.resolve() },
};

// Re-export ApiErrorException so callers don't need a second import to
// inspect status codes / structured codes when catching errors.
export { ApiErrorException };
